/**
 * Backorder Dialogs Manager
 * Handles "How it Works" and "Policy Terms" dialogs for backorder products
 */

type DialogCloseCallback = () => void;

interface FocusableElements {
  first: HTMLElement;
  last: HTMLElement;
  all: HTMLElement[];
}

interface Window {
  BackorderDialogs: BackorderDialogs;
}

class BackorderDialogs {
  private howWorksDialog: HTMLDialogElement | null = null;
  private policyTermsDialog: HTMLDialogElement | null = null;
  private policyTermsPendingCallback: DialogCloseCallback | undefined;

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  private setup(): void {
    this.setupHowWorksDialog();
    this.setupPolicyTermsDialog();
  }

  private setupHowWorksDialog(): void {
    this.howWorksDialog = document.getElementById('backorder_dialog__howworks') as HTMLDialogElement;
    
    if (!this.howWorksDialog) return;

    const closeButton = this.howWorksDialog.querySelector<HTMLButtonElement>('.dialog-close-button');
    
    if (!closeButton) {
      console.warn('Backorder How Works dialog: close button not found');
      return;
    }

    closeButton.addEventListener('click', () => {
      this.closeDialog(this.howWorksDialog);
    });

    // Prevent closing on backdrop click
    this.howWorksDialog.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
    });

    // Prevent closing with ESC key
    this.howWorksDialog.addEventListener('cancel', (e: Event) => {
      e.preventDefault();
    });
  }

  private setupPolicyTermsDialog(): void {
    this.policyTermsDialog = document.getElementById('backorder_dialog__policyterms') as HTMLDialogElement;
    
    if (!this.policyTermsDialog) return;

    const termsCheckbox = this.policyTermsDialog.querySelector<HTMLInputElement>("input[type='checkbox']");
    const agreeButton = this.policyTermsDialog.querySelector<HTMLButtonElement>('button.button');

    if (!termsCheckbox || !agreeButton) {
      console.warn('Backorder Policy Terms dialog: required elements not found');
      return;
    }

    termsCheckbox.addEventListener('change', () => {
      const isChecked = termsCheckbox.checked;
      agreeButton.disabled = !isChecked;
      agreeButton.setAttribute('aria-disabled', String(!isChecked));
    });

    agreeButton.addEventListener('click', () => {
      if (!termsCheckbox.checked) return;

      const cb = this.policyTermsPendingCallback;
      this.policyTermsPendingCallback = undefined;

      this.closeDialog(this.policyTermsDialog);

      if (typeof cb === 'function') cb();
    });

    // Prevent closing on backdrop click
    this.policyTermsDialog.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation();
    });

    // Prevent closing with ESC key
    this.policyTermsDialog.addEventListener('cancel', (e: Event) => {
      e.preventDefault();
    });
  }

  private closeDialog(dialog: HTMLDialogElement | null): void {
    if (dialog && typeof dialog.close === 'function') {
      dialog.close();
    }
  }

  /**
   * Opens the "How it Works" dialog
   * @param onCloseCallback - Optional callback to execute when dialog closes
   */
  public openHowWorks(onCloseCallback?: DialogCloseCallback): void {
    if (this.howWorksDialog && typeof this.howWorksDialog.showModal === 'function') {
      this.howWorksDialog.showModal();
      this.trapFocus(this.howWorksDialog);
      
      if (typeof onCloseCallback === 'function') {
        this.howWorksDialog.addEventListener('close', onCloseCallback, { once: true });
      }
    }
  }

  /**
   * Opens the "Policy Terms" dialog
   * @param onCloseCallback - Optional callback to execute when dialog closes
   */
  public openPolicyTerms(
    deliveryMessageOrOnClose?: string | DialogCloseCallback,
    onCloseCallback?: DialogCloseCallback
  ): void {
    if (!this.policyTermsDialog || typeof this.policyTermsDialog.showModal !== 'function') return;

    let deliveryMessage: string | undefined;
    let closeCb: DialogCloseCallback | undefined;

    if (typeof deliveryMessageOrOnClose === 'function') {
      closeCb = deliveryMessageOrOnClose;
    } else {
      deliveryMessage = deliveryMessageOrOnClose;
      closeCb = onCloseCallback;
    }

    this.setPolicyTermsDeliveryMessage(deliveryMessage);

    // Store callback to be executed ONLY when user clicks "Concordo"
    this.policyTermsPendingCallback = typeof closeCb === 'function' ? closeCb : undefined;

    this.policyTermsDialog.showModal();
    this.trapFocus(this.policyTermsDialog);

    // Clean up message + pending callback on close so they don't leak
    this.policyTermsDialog.addEventListener(
      'close',
      () => {
        this.setPolicyTermsDeliveryMessage(undefined);
        this.policyTermsPendingCallback = undefined;
      },
      { once: true }
    );
  }
  private setPolicyTermsDeliveryMessage(message?: string): void {
    if (!this.policyTermsDialog) return;

    const main = this.policyTermsDialog.querySelector<HTMLElement>('#backorder_policyterms_content');
    if (!main) return;

    const existing = main.querySelector<HTMLElement>('.backorder-delivery-message');

    if (!message) {
      if (existing) existing.remove();
      return;
    }

    const el = existing || document.createElement('p');
    el.classList.add('backorder-delivery-message');
    el.setAttribute('aria-live', 'polite');
    el.style.whiteSpace = 'pre-line';
    el.textContent = message;

    if (!existing) main.appendChild(el);
  }
  private trapFocus(dialog: HTMLDialogElement): void {
    const focusableElements = this.getFocusableElements(dialog);
    
    if (focusableElements.all.length === 0) return;

    focusableElements.first.focus();

    const handleTabKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === focusableElements.first) {
          e.preventDefault();
          focusableElements.last.focus();
        }
      } else {
        if (document.activeElement === focusableElements.last) {
          e.preventDefault();
          focusableElements.first.focus();
        }
      }
    };

    dialog.addEventListener('keydown', handleTabKey);
    
    dialog.addEventListener('close', () => {
      dialog.removeEventListener('keydown', handleTabKey);
    }, { once: true });
  }

  private getFocusableElements(dialog: HTMLDialogElement): FocusableElements {
    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const elements = Array.from(dialog.querySelectorAll<HTMLElement>(selector));
    
    return {
      all: elements,
      first: elements[0],
      last: elements[elements.length - 1]
    };
  }
}

// Initialize and expose globally
if (typeof window !== 'undefined') {
  window.BackorderDialogs = new BackorderDialogs();
}
